import { useEffect, useRef } from 'react';
import { Send } from 'lucide-react';
import { isReallyQueued } from '../lib/issueDispatch';
import type { AgentLaneStatus } from '../api/types';
import { getSseBus } from '../lib/sseBus';
import { pushToast, dismissToastByKey } from '../lib/toastStore';
import './StatusToast.css';

// Global, agent-INDEPENDENT notices — an agent got blocked, issue-api dispatched an
// issue (#1257), or auto-login gave up (#182). This component now holds only the
// DETECTION logic: each notice is pushed into the shared toast store (#194) and
// rendered by the single <GlobalToasts> host, so all in-app messaging shares one
// top-right stack + one look/behavior (slide from the right, manual-dismiss = confirm).

// The blocked-agent toast kept re-popping (#161, #163). The real cause was a reload
// artefact: the baseline got seeded on the first poll, which is often [] before data
// loads, so the already-blocked agent then read as "new". Fix: ignore empty polls,
// seed the baseline on the first REAL data, and remember which agents were announced —
// PERSISTED in localStorage so a given block announces exactly once across reloads.
// v3 (Alex 2026-07-11, "jede Stunde dieselbe Meldung"): identity is name+summary,
// not just name. A long-declared block FLAPS: live activity overrides it to
// 'working', idle falls back to 'blocked' — the v2 set deleted the name on every
// working phase, so the SAME block re-announced each cycle. Keying on the summary
// makes one block announce once however often the state flaps, while a NEW block
// (different reason) still announces.
const SEEN_KEY = 'ark-statustoast-seen-v3';
const loadSeen = (): Map<string, string> => {
  try { const r = localStorage.getItem(SEEN_KEY); return new Map<string, string>(r ? JSON.parse(r) : []); } catch { return new Map(); }
};
const saveSeen = (s: Map<string, string>) => {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...s].slice(-60))); } catch { /* ignore */ }
};

export function StatusToasts({ agents, onOpen, userId }: { agents: AgentLaneStatus[]; onOpen: (name: string) => void; userId?: string }) {
  const seen = useRef<Map<string, string>>(loadSeen()); // name → announced block-summary (persisted)
  const seeded = useRef(false);
  // Keep onOpen in a ref so the SSE subscriptions (below) never need it in their
  // deps — otherwise a new onOpen identity would tear down + re-subscribe the bus.
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  // Same ref trick for the current user id — read inside the []-deps SSE
  // subscription without tearing it down on every render.
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  // Blocked agents (#1251) — the one state worth interrupting the operator for.
  useEffect(() => {
    if (!agents.length) return; // never seed or toast on empty/loading polls (#163)
    let dirty = false;
    // NOTE: no longer forget on unblock — the seen entry (name→summary) survives the
    // working/idle flap of a still-declared block. A genuinely NEW block announces
    // because its summary differs from the remembered one.
    const blocked = agents.filter((a) => a.effective_state === 'blocked');
    if (!seeded.current) {
      // First REAL data: adopt existing blocks as already-seen. Announce only blocks
      // that arise WHILE watching — never the situation already ongoing on open.
      seeded.current = true;
      blocked.forEach((a) => {
        if (seen.current.get(a.name) !== (a.summary || '')) { seen.current.set(a.name, a.summary || ''); dirty = true; }
      });
      if (dirty) saveSeen(seen.current);
      return;
    }
    blocked.forEach((a) => {
      if (seen.current.get(a.name) === (a.summary || '')) return; // same block, announce once (#163 + v3 flap)
      seen.current.set(a.name, a.summary || '');
      dirty = true;
      const name = a.name;
      const key = `blocked:${name}`;
      pushToast({
        // Auto-hide after a generous window (Alex 2026-07-23: "nach einer
        // gewissen Zeit soll das wieder ausblenden") — the block is announced
        // ONCE (seen-set), so it won't re-pop, and the agent stays visibly
        // blocked in the list, so nothing is lost when the toast clears itself.
        variant: 'error', dismiss: 'auto', autoHideMs: 45000, dedupKey: key,
        title: <><strong>{name}</strong> ist blockiert</>,
        content: a.summary,
        onClick: () => { onOpenRef.current(name); dismissToastByKey(key); },
      });
    });
    if (dirty) saveSeen(seen.current);
  }, [agents]);

  // Dispatch notices (#1257): issue-api → cloud-api → SSE bus. Dedup per issue_id with a
  // COOLDOWN — a stuck issue that the queue re-dispatches every timeout would otherwise
  // re-toast endlessly (Alex's "Phase B prod test" loop). Blue + manual-dismiss so the
  // dispatch stays until Alex confirms he's seen it (a read-indicator, #194).
  const dispatchSeen = useRef<Map<string, number>>(new Map());
  const DISPATCH_COOLDOWN_MS = 10 * 60_000;
  useEffect(() => {
    const bus = getSseBus();
    return bus.subscribe('issue.dispatched', (ev) => {
      const d = ((ev as unknown as { data?: typeof ev }).data ?? ev);
      if (!d || d.issue_id == null) return;
      // Per-user scoping (Alex 2026-07-23: he was seeing issues OTHER users
      // filed — e.g. Jascha's — because _emit_federated broadcasts this event
      // to every SSE client on every node, unscoped). Suppress a dispatch whose
      // reporter isn't me. Note this is a UI filter, NOT a privacy boundary:
      // the event still reaches the browser. The real fix is server-side —
      // cloud-api's /api/sessions/events already knows the caller (optional_user,
      // the snapshot is user-scoped) but fans DELTAS out unfiltered, and
      // issue-api must put `reporter` in the emit payload first. Until it does,
      // `reporter` is absent and behaviour is unchanged — deliberately fail-OPEN,
      // since fail-closed would also hide the operator's own dispatches.
      const me = userIdRef.current;
      if (d.reporter && me && d.reporter !== me) return;
      const k = String(d.issue_id);
      const now = Date.now();
      const last = dispatchSeen.current.get(k);
      if (last != null && now - last < DISPATCH_COOLDOWN_MS) return;
      dispatchSeen.current.set(k, now);
      const name = (d.assignee || '').replace(/^agent:/, '');
      const issueId = d.issue_id;
      const key = `dispatch:${issueId}`;
      pushToast({
        // Auto-hide, but LONG (25s ≫ the 5s short status) with a visible countdown
        // bar, so the issue notice lingers but clears itself (Alex #202). Click to
        // open + dismiss early.
        variant: 'info', dismiss: 'auto', autoHideMs: 25000, icon: null, dedupKey: key,
        content: (
          <>
            <div className="dt-top">
              <div className="dt-avatar"><Send size={18} /></div>
              <div className="dt-main">
                <div className="dt-head">
                  <span className="dt-issue">Issue #{issueId}</span>
                  <span className="dt-pill">{name}</span>
                  {isReallyQueued(d) && <span className="dt-pill dt-pill-queue">Queue</span>}
                  <span className="dt-now">jetzt</span>
                </div>
                {d.title && <div className="dt-title">{d.title}</div>}
              </div>
            </div>
            <div className="dt-action">Details öffnen →</div>
          </>
        ),
        onClick: () => { onOpenRef.current(name); dismissToastByKey(key); },
      });
    });
  }, []);

  // Auto-login gave up after N attempts (Cloud #182) — red operator alert, manual /login
  // needed. Auto-hides after a generous window (Alex 2026-07-23) — announced once per
  // (name,ts), and the agent stays visibly needs_login in the list, so the alert clearing
  // itself loses nothing.
  const loginSeen = useRef<Set<string>>(new Set());
  useEffect(() => {
    const bus = getSseBus();
    return bus.subscribe('agent.login_failed', (ev) => {
      const d = ((ev as unknown as { data?: typeof ev }).data ?? ev);
      if (!d || !d.name) return;
      const k = `${d.name}::${d.ts ?? ''}`;
      if (loginSeen.current.has(k)) return;
      loginSeen.current.add(k);
      const name = d.name;
      const key = `login:${name}`;
      pushToast({
        variant: 'error', dismiss: 'auto', autoHideMs: 45000, dedupKey: key,
        title: <><strong>{name}</strong>: Auto-Login gestoppt</>,
        content: `Nach ${d.attempts ?? 3} Fehlversuchen — manuelles /login nötig`,
        onClick: () => { onOpenRef.current(name); dismissToastByKey(key); },
      });
    });
  }, []);

  return null; // rendering is handled by the shared <GlobalToasts> host (#194)
}
